import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  HStack,
  VStack,
  RadioGroup,
  Radio,
  Box,
  Select as ChakraSelect,
} from "@chakra-ui/react";

type ObjectListFieldProps = {
  field: any;
  value?: any;
  readOnly?: boolean;
  invalid?: boolean;
};

type OptionLike = string | { label: string; value: string };

const ObjectListField: React.FC<ObjectListFieldProps> = ({
  field,
  value = [],
  readOnly = false,
  invalid = false,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [singleMode, setSingleMode] = useState<boolean>(false);
  const [mode, setMode] = useState<"na" | "val">("val");

  const isVoid = (v: any) => v == null || (typeof v === "string" && v.trim().length === 0);
  const isItemVoid = (obj: any) => obj && typeof obj === "object" && Object.values(obj).every(isVoid);
  const isArrayOfVoidItems = (arr: any[]) => Array.isArray(arr) && arr.length > 0 && arr.every(isItemVoid);

  const emptyTemplate = useMemo(
    () =>
      field.subfields.reduce(
        (acc: any, sub: any) => ({
          ...acc,
          [sub.name]: "",
        }),
        {}
      ),
    [field.subfields]
  );

  const createEmptyItem = useCallback(() => ({ ...emptyTemplate }), [emptyTemplate]);

  useEffect(() => {
    if (value === "N/A") {
      setMode("na");
      setItems([createEmptyItem()]);
      setSingleMode(true);
      return;
    }
    setMode("val");
    if (Array.isArray(value) && value.length > 0) {
      if (isArrayOfVoidItems(value)) {
        setItems([createEmptyItem()]);
        setSingleMode(true);
      } else {
        setItems(value.map((v) => ({ ...emptyTemplate, ...v })));
        setSingleMode(false);
      }
    } else {
      setItems(readOnly ? [] : [createEmptyItem()]);
      setSingleMode(!readOnly);
    }
  }, [value, readOnly, createEmptyItem, emptyTemplate]);

  const handleModeChange = (next: string) => {
    if (readOnly) return;
    const m = (next === "na" ? "na" : "val") as "na" | "val";
    setMode(m);
    if (m === "val") {
      if (!Array.isArray(items) || items.length === 0) {
        setItems([createEmptyItem()]);
        setSingleMode(true);
      }
    } else {
      if (!Array.isArray(items) || items.length === 0) {
        setItems([createEmptyItem()]);
      }
      setSingleMode(true);
    }
  };

  const handleAddItem = () => {
    if (mode === "na" || readOnly) return;
    const next = [...items, createEmptyItem()];
    setItems(next);
    if (singleMode && next.length > 1) setSingleMode(false);
  };

  const handleRemoveItem = (index: number) => {
    if (mode === "na" || readOnly) return;
    const updated = items.filter((_, i) => i !== index);
    if (updated.length === 0) {
      setItems([createEmptyItem()]);
      setSingleMode(true);
    } else {
      setItems(updated);
      setSingleMode(false);
    }
  };

  const handleChange = (index: number, subfieldName: string, v: string) => {
    const updated = items.map((item, i) => (i === index ? { ...item, [subfieldName]: v } : item));
    setItems(updated);
  };

  const isSelectField = (sf: any) =>
    (sf.type && String(sf.type).toLowerCase() === "select") ||
    (sf.htmlType && String(sf.htmlType).toLowerCase() === "select");

  const normalizeOptions = (opts?: OptionLike[]): { label: string; value: string }[] => {
    if (!Array.isArray(opts)) return [];
    return opts.map((o) =>
      typeof o === "string" ? { label: o, value: o } : { label: o.label ?? o.value, value: o.value ?? o.label }
    );
  };

  const disabled = readOnly || mode === "na";
  const canRemove = (idx: number) => !disabled && (!singleMode || (Array.isArray(items) && items.length > 1));
  const addLabel =
    (field.singleLabel && typeof field.singleLabel === "string"
      ? field.singleLabel.charAt(0).toUpperCase() + field.singleLabel.slice(1).toLowerCase()
      : "Item") + " +";

  const effectiveItems = useMemo(() => {
    if (mode === "na") return [];
    const trimmed = items
      .map((it) => {
        const out: Record<string, any> = {};
        for (const s of field.subfields) out[s.name] = it[s.name] ?? "";
        return out;
      })
      .filter((it) => !isItemVoid(it));
    return trimmed;
  }, [items, field.subfields, mode]);

  const aggregatedValue = useMemo(() => {
    if (mode === "na") return "N/A";
    if (effectiveItems.length === 0) return "N/A";
    return effectiveItems;
  }, [mode, effectiveItems]);

  return (
    <FormControl mb={4} isInvalid={!!invalid} data-field-name={field?.name}>
      <FormLabel>
        <b>{field.label}</b> {!field.nullable && <span style={{ color: "red" }}>*</span>}
      </FormLabel>

      <RadioGroup value={mode} onChange={handleModeChange} isDisabled={readOnly}>
        {field.nullable === true && (
          <HStack mb={2}>
            <Radio value="na">TBD</Radio>
          </HStack>
        )}

        <HStack align="start" spacing={2}>
          {field.nullable === true && <Radio value="val" aria-label="Provide values" mt="6px" />}

          <Box
            border="1px solid #ccc"
            borderRadius="md"
            p={3}
            flex="1"
            opacity={disabled ? 0.6 : 1}
            pointerEvents={disabled ? "none" : "auto"}
            className={invalid ? "invalid-box" : ""}
          >
            <VStack spacing={4} align="start">
              {items.map((item, index) => (
                <VStack key={index} spacing={2} p={4} width="100%">
                  {field.subfields.map((subfield: any) => {
                    const options = normalizeOptions(subfield.options);
                    const isSelect = isSelectField(subfield);
                    const inputType = subfield.htmlType || subfield.type || "text";

                    return (
                      <HStack key={subfield.name} spacing={2} width="100%">
                        <FormControl isRequired={subfield.required}>
                          <FormLabel>{subfield.label}</FormLabel>

                          {isSelect ? (
                            <ChakraSelect
                              name={`${field.name}[${index}][${subfield.name}]`}
                              value={item[subfield.name] ?? ""}
                              onChange={(e) => handleChange(index, subfield.name, e.target.value)}
                              isDisabled={disabled}
                            >
                              <option value="">Select...</option>
                              {options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </ChakraSelect>
                          ) : (
                            <Input
                              name={`${field.name}[${index}][${subfield.name}]`}
                              type={inputType}
                              value={item[subfield.name] ?? ""}
                              onChange={(e) => handleChange(index, subfield.name, e.target.value)}
                              isDisabled={disabled}
                              readOnly={readOnly}
                            />
                          )}
                        </FormControl>
                      </HStack>
                    );
                  })}

                  <HStack spacing={2} width="100%" align="center">
                    <div className="multi-object-actions-container">
                      <button
                        aria-label="Remove element"
                        onClick={() => handleRemoveItem(index)}
                        className="el-btn el-btn-danger"
                        disabled={!canRemove(index)}
                        type="button"
                      >
                        Remove item
                      </button>
                      {items.length === index + 1 && (
                        <button type="button" className="el-btn" onClick={handleAddItem} disabled={disabled}>
                          {addLabel}
                        </button>
                      )}
                    </div>
                  </HStack>
                </VStack>
              ))}
            </VStack>
          </Box>
        </HStack>
      </RadioGroup>

      <input
        type="hidden"
        name={field.name}
        value={typeof aggregatedValue === "string" ? "N/A" : JSON.stringify(aggregatedValue)}
      />
      {mode === "na" && <input type="hidden" name={`${field.name}__na`} value="N/A" />}
    </FormControl>
  );
};

export default ObjectListField;
